// core/services/commentsService.ts — B4a: API-backed comments.
//
// Thin fetch wrappers over the DRF /api/posts/{id}/comments/ and /api/comments/{id}/
// endpoints. Reads hydrate the comment store (keyed by post); mutations patch it.
// Signatures + AddCommentFields/AddReplyFields are unchanged from the in-memory version.

import type { Comment } from '@/core/models/comment';
import { useCommentStore } from '@/core/stores/commentStore';
import { seedComments } from '@/core/data/comments';
import { api } from '@/core/lib/apiClient';
import { useSessionStore } from '@/core/stores/sessionStore';

let sessionGeneration = 0;
let readSequence = 0;
const reads = new Map<string, number>();
useSessionStore.subscribe((next, previous) => {
  const owner = (s: typeof next) => s.accounts.find((a) => a.useThisAsActive)?.userId;
  if (owner(next) === owner(previous)) return;
  sessionGeneration++;
  reads.clear();
  useCommentStore.getState().setComments([]);
});

/** Fields accepted by addComment (top-level). */
export interface AddCommentFields {
  postId: string;
  body: string;
  /** Handle of the author (e.g. '@neo'). Kept for signature compatibility (server uses request.user). */
  authorHandle: string;
}

/** Fields accepted by addReply (nested; parentId must be a top-level comment id). */
export interface AddReplyFields {
  postId: string;
  parentId: string;
  targetCommentId?: string;
  body: string;
  authorHandle: string;
  replyingToHandle?: string;
}

/** Replace this post's comments in the store, leaving other posts' comments intact. */
function hydrate(postId: string, list: Comment[]): void {
  const others = useCommentStore.getState().comments.filter((c) => c.postId !== postId);
  useCommentStore.getState().setComments([...others, ...list]);
}

export const commentsService = {
  captureSession: () => sessionGeneration,
  acceptCreatedComment(comment: Comment, generation: number): boolean {
    if (generation !== sessionGeneration) return false;
    reads.delete(comment.postId);
    useCommentStore.getState().addComment(comment);
    return true;
  },
  async forPost(postId: string): Promise<Comment[]> {
    const generation = sessionGeneration;
    const read = ++readSequence;
    reads.set(postId, read);
    const list = await api<Comment[]>(`/posts/${postId}/comments/`);
    if (generation !== sessionGeneration || reads.get(postId) !== read) return [];
    hydrate(postId, list);
    return list;
  },

  async topLevelForPost(postId: string): Promise<Comment[]> {
    const list = await commentsService.forPost(postId);
    return list.filter((c) => c.parentId === null);
  },

  async repliesFor(parentId: string): Promise<Comment[]> {
    return useCommentStore.getState().comments.filter((c) => c.parentId === parentId);
  },

  async addComment(fields: AddCommentFields): Promise<Comment> {
    const generation = sessionGeneration;
    const c = await api<Comment>(`/posts/${fields.postId}/comments/`, {
      method: 'POST',
      body: { body: fields.body },
    });
    if (generation === sessionGeneration) {
      reads.delete(fields.postId);
      useCommentStore.getState().addComment(c);
    }
    return c;
  },

  async addReply(fields: AddReplyFields): Promise<Comment> {
    const generation = sessionGeneration;
    const c = await api<Comment>(`/posts/${fields.postId}/comments/`, {
      method: 'POST',
      body: {
        body: fields.body,
        parentId: fields.parentId,
        targetCommentId: fields.targetCommentId,
        replyingToHandle: fields.replyingToHandle,
      },
    });
    if (generation === sessionGeneration) {
      reads.delete(fields.postId);
      useCommentStore.getState().addComment(c);
    }
    return c;
  },

  async updateComment(id: string, newBody: string, reason?: string): Promise<Comment | null> {
    const generation = sessionGeneration;
    try {
      const c = await api<Comment>(`/comments/${id}/`, {
        method: 'PATCH',
        body: { body: newBody, ...(reason ? { reason } : {}) },
      });
      if (generation !== sessionGeneration) return null;
      reads.delete(c.postId);
      useCommentStore.getState().patchComment(id, c);
      return c;
    } catch {
      return null;
    }
  },

  async deleteComment(id: string): Promise<Comment | null> {
    const generation = sessionGeneration;
    try {
      const c = await api<Comment>(`/comments/${id}/`, { method: 'DELETE' });
      if (generation !== sessionGeneration) return null;
      reads.delete(c.postId);
      useCommentStore.getState().patchComment(id, c);
      return c;
    } catch {
      return null;
    }
  },

  async toggleCommentLike(id: string): Promise<Comment | null> {
    const generation = sessionGeneration;
    try {
      const c = await api<Comment>(`/comments/${id}/like/`, { method: 'POST' });
      if (generation !== sessionGeneration) return null;
      useCommentStore.getState().patchComment(id, c);
      return c;
    } catch {
      return null;
    }
  },

  _resetForTest(): void {
    useCommentStore
      .getState()
      .setComments(seedComments.map((c) => ({ ...c, editHistory: [...c.editHistory] })));
  },
} as const;
